<script setup lang="ts">
import { ref, watch } from "vue";
import PhoneNumber from "@/components/phones/PhoneNumber.vue";
import ChatInfoDriverPanel from "@/views/chats/components/info/ChatInfoDriverPanel.vue";
import ChatInfoOwnerPanel from "@/views/chats/components/info/ChatInfoOwnerPanel.vue";
import ChatInfoCoordinatorPanel from "@/views/chats/components/info/ChatInfoCoordinatorPanel.vue";
import ChatInfoUserInfo from "@/views/chats/components/info/ChatInfoUserInfo.vue";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import type { Response } from "@/api/client";
import type { UserDto } from "@/api/dto/user.dto";
import type { ChatStateThread } from "@/store/chat.store";
import type { ChatInfoUser } from "@/types/chat";

const props = defineProps<{
  thread: ChatStateThread;
}>();

const { showAPIError } = useError();
const api = new API();

const user = ref<ChatInfoUser | null>(null);

function handleError(resp: Response<unknown>) {
  if (resp.errorData?.detail) {
    showAPIError(resp);
  }
}

async function loadUser() {
  if (!props.thread.userId) {
    return;
  }

  try {
    const baseResp = await api.users.findUserById(props.thread.userId);

    if (!baseResp.success || !baseResp.data) {
      handleError(baseResp);
      return;
    }

    const loadedUser: ChatInfoUser = {
      coordinators: [],
      owners: [],
      ...(baseResp.data as unknown as UserDto),
    };

    if (loadedUser.isDriver) {
      const driverResp = await api.users.findDriverById(loadedUser.id);

      if (driverResp.success && driverResp.data) {
        Object.assign(loadedUser, driverResp.data);
      } else {
        handleError(driverResp);
      }

      if (loadedUser.ownerId) {
        const coordResp = await api.users.findOwnerCoordinators(loadedUser.ownerId);

        if (coordResp.success && coordResp.data) {
          loadedUser.coordinators = coordResp.data;
        } else {
          handleError(coordResp);
        }
      }
    }

    if (loadedUser.isOwner) {
      const coordResp = await api.users.findOwnerCoordinators(loadedUser.id);

      if (coordResp.success && coordResp.data) {
        loadedUser.coordinators = coordResp.data;
      } else {
        handleError(coordResp);
      }
    }

    if (loadedUser.isCoordinator) {
      const ownersResp = await api.users.findCoordinatorOwners(loadedUser.id);

      if (ownersResp.success && ownersResp.data) {
        loadedUser.owners = ownersResp.data;
      } else {
        handleError(ownersResp);
      }
    }

    user.value = loadedUser;
  } catch (error) {
    console.error(error);
  }
}

watch(
  () => props.thread.user,
  (newValue, prevValue) => {
    const hasUserChanged = newValue && prevValue && JSON.stringify(newValue) !== JSON.stringify(prevValue);

    if (!user.value || hasUserChanged) {
      loadUser();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div :class="user?.emergencyContactPhone ? 'emergency-phone' : ''" class="height-100 position-relative">
    <div class="d-flex flex-column height-100">
      <div class="info-panel__inner position-relative height-100 flex-grow-1">
        <div v-if="user" class="info-panel__wrapper scroll-style">
          <chat-info-user-info :user="user" />

          <div class="panel-info__blocks">
            <chat-info-driver-panel :user="user" />
            <chat-info-owner-panel :user="user" />
            <chat-info-coordinator-panel :user="user" />
          </div>
        </div>

        <div v-if="user?.emergencyContactPhone" class="emergency-contact pa-4 rounded rounded-t-0">
          <div class="subheading mb-2 font-weight-medium text-grey-darken-3">Emergency contact</div>
          <a :href="`callto: ${user.emergencyContactPhone}`" class="d-flex align-center text-decoration-none">
            <span class="bg-grey-lighten-4 rounded-circle user-contact__icon mr-3 d-flex align-center justify-center">
              <v-icon class="text-grey-darken-1" size="14">mdi-phone-outline</v-icon>
            </span>
            <phone-number :number="user.emergencyContactPhone" class="caption text-primary" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
dt {
  font-weight: bold;
}

dd {
  margin-bottom: 7px;
}

.icon-block--medium {
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
}

/*.link-icon {
  display: inline-block;
  width: 11px;
  height: 11px;
  background: url(@/assets/images/open-link.svg) no-repeat;
}*/
.user-contact__icon {
  width: 24px;
  flex: 0 0 24px;
  height: 24px;
  cursor: pointer;
}

.panel-info__block-content {
  background: #f8f9fa;

  .content {
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.16);
  }
}

.info-panel__wrapper {
  // height: calc(100% - 100px);
  height: 100%;
  overflow: hidden;
  overflow-y: auto;
  padding: 24px;
}

.info-panel__inner {
  padding-bottom: 32px;
}

.emergency-phone .info-panel__inner {
  padding-bottom: 90px;
}

.emergency-contact {
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  background: #ffffff;
  box-shadow: 0px -10px 20px rgba(18, 38, 63, 0.04);
}

.trucks-dropdown__btn {
  height: auto !important;
  min-width: auto !important;
  padding: 0 !important;

  &:before {
    display: none;
  }
}

.v-list {
  padding: 6px 10px !important;
  box-shadow: none !important;
}

.v-list-item {
  padding: 0 !important;
}

.v-list-item {
  &:not(:last-child) {
    border-bottom: 1px solid #f0f1f4;
  }
}

.track-block {
  width: 100%;
  padding: 12px 0;
}

.v-menu__content {
  box-shadow: 0 10px 20px rgba(18, 38, 63, 0.04) !important;
  border-radius: 4px;
}

.short-line {
  position: relative;
  margin: 12px 0;

  &:before {
    content: "";
    display: block;
    max-width: 55%;
    height: 1px;
    border-bottom: 1px solid #dee2e6;
    margin: 0 auto;
  }
}

.day {
  position: relative;

  &:after {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    display: block;
    content: "";
    width: 100%;
    height: 1px;
    border: 1px solid #dee2e6;
  }

  span {
    background: #f8f9fa;
    display: inline-block;
    position: relative;
    z-index: 1;
  }
}

.bordered {
  border: solid 1px #616161;
}

.back-btn {
  display: none;
  @media (max-width: 991px) {
    display: block !important;
  }
}

.message-textarea {
  .v-input__slot {
    border: 1px solid #bdbdbd !important;
  }
}

.currency-icon {
  width: 18px;
  flex: 0 0 18px;
  height: 18px;
  font-size: 13px;
  display: inline-block;
  text-align: center;
  border-radius: 50%;
  background: url(@/assets/images/currency-icon.svg) no-repeat;
  background-size: 18px;
}
</style>
